import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingParty, EFilingAttorney } from '@lds-efsp/shared-contracts';
import { useEFiling } from '../contexts/EFilingContext';
import AddPartyModal from './AddPartyModal';
import AddAttorneyModal from './AddAttorneyModal';
import PartyCards from './PartyCards';

interface CaseParticipantsTabProps {
  brand: BrandConfig;
}

const CaseParticipantsTab: React.FC<CaseParticipantsTabProps> = ({ brand }) => {
  const { wizardState, addParty, removeParty, updateParty, addAttorney, removeAttorney, setAttorneys, confirmFlowEnabled } = useEFiling();
  const { parties, attorneys } = wizardState;
  // WOL-344: pass isSubsequent so AddAttorneyModal can show role + address fields
  const isSubsequent = wizardState.orderInfo?.filingType === 'subsequent-filing';

  const [showAddParty, setShowAddParty] = useState(false);
  const [editingParty, setEditingParty] = useState<EFilingParty | undefined>();
  const [showAddAttorney, setShowAddAttorney] = useState(false);
  const [editingAttorney, setEditingAttorney] = useState<EFilingAttorney | undefined>();

  // Validation checks
  const leadClientCount = parties.filter(p => p.isLeadClient).length;
  const hasSelfRepOrAttorney = parties.some(p => p.isSelfRepresented) || attorneys.length > 0;

  const handleSaveParty = (party: EFilingParty) => {
    // Enforce single lead client
    if (party.isLeadClient && leadClientCount > 0) {
      const existingLead = parties.find(p => p.isLeadClient && p.id !== party.id);
      if (existingLead) {
        updateParty(existingLead.id, { isLeadClient: false });
      }
    }

    if (editingParty) {
      // Preserve the edited entry's existing source — the modal doesn't carry a
      // source field, so party.source may be undefined; don't silently reclassify
      // a case-roster/extraction entry to manual (SOR-223 R1).
      updateParty(party.id, { ...party, source: party.source ?? editingParty.source });
    } else {
      // A genuinely user-added party is manual-sourced (SOR-223 R1).
      addParty({ ...party, source: 'manual' });
    }
    setShowAddParty(false);
    setEditingParty(undefined);
  };

  const handleEditParty = (party: EFilingParty) => {
    setEditingParty(party);
    setShowAddParty(true);
  };

  const handleDeleteParty = (partyId: string) => {
    removeParty(partyId);
    // Clean up attorney references to this party
    for (const att of attorneys) {
      if (att.representedPartyIds?.includes(partyId)) {
        const updated = { ...att, representedPartyIds: att.representedPartyIds.filter(id => id !== partyId) };
        setAttorneys(attorneys.map(a => a.id === att.id ? updated : a));
      }
    }
  };

  const handleSaveAttorney = (attorney: EFilingAttorney) => {
    if (editingAttorney) {
      // Preserve the edited attorney's existing source (SOR-223 R1).
      const merged = { ...attorney, source: attorney.source ?? editingAttorney.source };
      setAttorneys(attorneys.map(a => a.id === attorney.id ? merged : a));
    } else {
      // A genuinely user-added attorney is manual-sourced (SOR-223 R1).
      addAttorney({ ...attorney, source: 'manual' });
    }
    setShowAddAttorney(false);
    setEditingAttorney(undefined);
  };

  const handleEditAttorney = (attorney: EFilingAttorney) => {
    setEditingAttorney(attorney);
    setShowAddAttorney(true);
  };

  const handleAddParty = () => {
    setEditingParty(undefined);
    setShowAddParty(true);
  };

  const handleAddAttorney = () => {
    setEditingAttorney(undefined);
    setShowAddAttorney(true);
  };

  const getPartyDisplayName = (party: EFilingParty): string => {
    if (party.partyType === 'person') {
      return [party.firstName, party.middleName, party.lastName]
        .filter(Boolean)
        .join(' ');
    }
    return party.organizationName || '';
  };

  return (
    // The id is the scroll anchor for participant-class Next blockers (e.g.
    // R103's ud-defendant-address-required) — see NEXT_BLOCKER_PRESENTATION.
    <div id="participants-section">
      <div className="flex justify-between items-center mb-4">
        <h2
          className="text-lg font-semibold"
          style={{ color: brand.colors.text }}
        >
          Case Participants
        </h2>
      </div>

      {/* Validation messages */}
      {parties.length === 0 && (
        <div
          className="p-3 rounded-lg border text-sm mb-4"
          style={{
            borderColor: brand.colors.warning || '#F59E0B',
            backgroundColor: `${brand.colors.warning || '#F59E0B'}10`,
            color: brand.colors.text,
          }}
        >
          At least one participant is required to proceed.
        </div>
      )}

      {leadClientCount > 1 && (
        <div
          className="p-3 rounded-lg border text-sm mb-4"
          style={{
            borderColor: '#EF4444',
            backgroundColor: '#EF444410',
            color: '#EF4444',
          }}
        >
          Please select only one Client per order for Billing purposes.
        </div>
      )}

      {parties.length > 0 && !hasSelfRepOrAttorney && (
        <div
          className="p-3 rounded-lg border text-sm mb-4"
          style={{
            borderColor: brand.colors.warning || '#F59E0B',
            backgroundColor: `${brand.colors.warning || '#F59E0B'}10`,
            color: brand.colors.text,
          }}
        >
          Please add at least one self-represented participant or an attorney.
        </div>
      )}

      <PartyCards
        brand={brand}
        parties={parties}
        attorneys={attorneys}
        getPartyDisplayName={getPartyDisplayName}
        onEditParty={handleEditParty}
        onDeleteParty={handleDeleteParty}
        onEditAttorney={handleEditAttorney}
        onDeleteAttorney={removeAttorney}
        onAddParty={handleAddParty}
        onAddAttorney={handleAddAttorney}
      />

      {/* Add Party Modal */}
      {showAddParty && (
        <AddPartyModal
          brand={brand}
          county={wizardState.orderInfo.county}
          caseCategoryCode={wizardState.caseInfo.caseCategoryCode}
          showMailingAddress={confirmFlowEnabled}
          onSave={handleSaveParty}
          onClose={() => {
            setShowAddParty(false);
            setEditingParty(undefined);
          }}
          existingParty={editingParty}
        />
      )}

      {/* Add Attorney Modal */}
      {showAddAttorney && (
        <AddAttorneyModal
          brand={brand}
          county={wizardState.orderInfo.county}
          parties={parties}
          onSave={handleSaveAttorney}
          onClose={() => {
            setShowAddAttorney(false);
            setEditingAttorney(undefined);
          }}
          existingAttorney={editingAttorney}
          isSubsequent={isSubsequent}
        />
      )}
    </div>
  );
};

export default CaseParticipantsTab;
